import React from "react";
import { Compass, HardHat, Cpu } from "lucide-react";

const services = [
  {
    icon: Compass,
    label: "Service · 01",
    title: "Cross-Border Landlord Advisory",
    subtitle: "Spatial Positioning Framework",
    body:
      "Data-driven rental-yield optimisation, layout redesigns for premium executive lets, and municipal compliance auditing — delivered as an actionable blueprint.",
    bullets: [
      "Yield model per unit & micro-market",
      "Spatial redesign for premium let-ability",
      "Regulatory & tenancy compliance audit",
    ],
    fee: "Flat-rate engagement · $2.5k – $5k / property",
  },
  {
    icon: HardHat,
    label: "Service · 02",
    title: "Remote Project Coordination",
    subtitle: "Auditing & Vendor Management",
    body:
      "Independent oversight of refurbishments and capital works. We source, vet, and monitor contractors — without ever taking custody of client funds.",
    bullets: [
      "Contractor sourcing & vetting",
      "Milestone, quality & material audits",
      "Invoice verification & dispute mediation",
    ],
    fee: "Performance-based · 12% of contract value",
  },
  {
    icon: Cpu,
    label: "Service · 03",
    title: "PropTech Infrastructure",
    subtitle: "Onboarding & Portfolio Retainers",
    body:
      "Property Management Systems, smart-lock access, and supply-chain logistics installed and maintained — so absentee ownership behaves like local ownership.",
    bullets: [
      "PMS, smart-lock & contactless setup",
      "Cleaning, maintenance & logistics chain",
      "Monthly remote performance audit",
    ],
    fee: "Retainer · $1,500 / month / unit",
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-[#EFEBE4] relative">
      <div className="grain" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <span className="text-xs uppercase tracking-[0.28em] text-[#6E5A4E]">The Practice</span>
            <h2 className="font-serif-display mt-6 text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-[#2B211A] leading-[1.05]">
              Three pillars of <span className="italic text-[#8C735D]">independent</span> oversight.
            </h2>
          </div>
          <p className="md:max-w-sm text-[#6E5A4E] leading-relaxed">
            Each pillar can be engaged individually or composed into a single mandate covering
            acquisition, refurbishment, and ongoing portfolio operation.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <article
                key={s.title}
                data-testid={`service-card-${i}`}
                className="group relative bg-[#F9F8F6] border border-[#D6CDBB] p-8 md:p-10 flex flex-col transition-all duration-500 hover:bg-[#2B211A] hover:border-[#2B211A]"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-[0.28em] text-[#8C735D] group-hover:text-[#B89A7E] transition-colors">
                    {s.label}
                  </span>
                  <Icon strokeWidth={1.25} className="w-7 h-7 text-[#8C735D] group-hover:text-[#EFEBE4] transition-colors" />
                </div>

                <h3 className="font-serif-display mt-10 text-3xl md:text-[34px] leading-[1.1] text-[#2B211A] group-hover:text-[#F9F8F6] transition-colors">
                  {s.title}
                </h3>
                <p className="font-serif-display italic text-lg text-[#8C735D] mt-2 group-hover:text-[#B89A7E] transition-colors">
                  {s.subtitle}
                </p>

                <p className="mt-6 text-[#6E5A4E] leading-relaxed text-[15px] group-hover:text-[#D6CDBB] transition-colors">
                  {s.body}
                </p>

                <ul className="mt-7 space-y-2.5 text-[14px] text-[#2B211A] group-hover:text-[#EFEBE4] transition-colors">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex gap-3">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#8C735D] group-hover:bg-[#B89A7E] shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-10 pt-6 border-t border-[#D6CDBB] group-hover:border-[#6B5343] transition-colors">
                  <span className="text-xs uppercase tracking-[0.24em] text-[#6E5A4E] group-hover:text-[#B89A7E] transition-colors">
                    {s.fee}
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
